<script setup lang="ts">
import type { SelectionLine } from '@/helpers/selectionRows'

import PriceDisplay from '@/components/elements/PriceDisplay.vue'

export type SelectionLinesProps = {
  lines: SelectionLine[]
}

defineProps<SelectionLinesProps>()
</script>

<template>
  <ul class="selection-lines">
    <li v-for="(line, index) in lines" :key="index" class="line" :class="{ answered: line.value !== undefined }">
      <span class="label-group">
        <span class="label">
          <span class="label-text">{{ line.label }}</span>

          <template v-if="line.quantity !== undefined">
            <span class="multiplier">&times;</span>
            <span class="quantity">{{ line.quantity }}</span>
          </template>
        </span>

        <span v-if="line.quantity === undefined && line.value" class="value">{{ line.value }}</span>
      </span>

      <span v-if="line.price !== undefined" class="price">
        <PriceDisplay :value="line.price" />
      </span>
    </li>
  </ul>
</template>

<style scoped>
.selection-lines {
  list-style: none;
  padding-left: 0;

  .line {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--padding-tight);
    padding-left: var(--padding-tight);

    .label-group {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .label {
      display: flex;
      align-items: flex-start;
      gap: var(--padding-tight);
      min-width: 0;

      .label-text,
      .quantity {
        font-weight: var(--font-weight-semibold);
      }
    }

    .quantity,
    .price {
      font-variant-numeric: tabular-nums;
    }

    .value {
      padding-left: var(--padding-tight);
    }

    /* A survey answer reads the other way round to a quantity: the question sets up the answer. */
    &.answered {
      .label-text {
        font-weight: var(--font-weight-regular);
        color: var(--text-secondary);
      }

      .value {
        font-weight: var(--font-weight-semibold);
      }
    }

    .price {
      white-space: nowrap;
    }
  }
}
</style>
